<template>
  <div>
    <component v-if="showPopup" :show="showPopup !== null" :is="showPopup" />
    <v-dialog
      :value="true"
      @input="$emit('input', $event)"
      :fullscreen="true"
      scrollable
    >
      <v-card>
        <div class="menu-user-banner d-flex align-center">
          <v-btn
            @click="$emit('close')"
            data-test-user-menu-back
            icon
            color="white"
          >
            <v-icon>mdi-arrow-left</v-icon>
          </v-btn>
          <span class="font-weight-bold ml-4">User profile</span>
        </div>
        <v-list dense>
          <v-list-item>
            <v-list-item-avatar>
              <v-avatar size="40" color="primary">
                <v-img
                  v-if="getPicture()"
                  cover
                  width="40"
                  height="40"
                  :src="getPicture()"
                />
                <span class="white--text" v-else>{{ firstLetters }}</span>
              </v-avatar>
            </v-list-item-avatar>
            <v-list-item-content>
              <v-list-item-title class="headline font-weight-medium">
                <div class="caption-2">
                  {{ user.login }}
                </div>
              </v-list-item-title>
              <v-list-item-subtitle>
                <div class="body-2 text--secondary">
                  {{ user.mEmail }}
                </div>
              </v-list-item-subtitle>
            </v-list-item-content>
          </v-list-item>
        </v-list>
        <v-divider></v-divider>
        <v-list dense>
          <v-list-item
            v-for="item in items"
            :key="item.id"
            :data-test-menu="item.id"
            @click="item.action(item)"
          >
            <v-list-item-action>
              <v-icon>{{ item.icon }}</v-icon>
            </v-list-item-action>
            <v-list-item-content>
              <v-list-item-title>
                {{ item.title }}
              </v-list-item-title>
            </v-list-item-content>
          </v-list-item>
        </v-list>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import useCommonModal from '@/compositions/useCommonModal';
import useMedia from '@/compositions/useMedia';

import {
  computed,
  inject,
  onMounted,
  reactive,
  ref,
  toRefs,
  watch
} from '@vue/composition-api';
import useAuth from '@/api/auth';

export default {
  name: 'MenuUserPopup',
  components: {
    notySettings: () => import('../popups/NotificationSettings'),
    programSettings: () => import('../popups/ProgramSettings'),
    userEdit: () => import('@/components/app-entity/modals/CreateUser.vue')
  },
  setup(props, { emit, root }) {
    const modalComposition = useCommonModal();
    const { getImageById } = useMedia();
    const user = inject('user');
    const currentProfile = ref(null);
    const { profile, loadProfileAsync } = useAuth();

    const localState = reactive({
      showPopup: null,
      items: [
        {
          id: 'edit_profile',
          title: 'Edit account',
          icon: 'mdi-pencil',
          action: () => {
            modalComposition.modal.show({
              component: () =>
                import('@/components/app-entity/modals/AppDialog.vue'),
              bind: {
                nameModal: 'UserEdit'
              },
              slots: {
                default: {
                  component: () =>
                    import('@/components/app-entity/modals/CreateUser.vue'),
                  bind: {
                    entity: user.value
                  },
                  on: {
                    close() {
                      emit('close');
                      localState.showPopup = null;
                      modalComposition.modal.hide('UserEdit');
                    }
                  }
                }
              }
            });
          }
        },
        {
          id: 'program_settings',
          title: 'Program settings',
          icon: 'mdi-cog',
          action: () => {
            modalComposition.modal.show({
              component: () =>
                import('@/components/app-entity/modals/AppDialog.vue'),
              bind: {
                nameModal: 'ProgramSettings'
              },
              slots: {
                default: {
                  component: () => import('../popups/ProgramSettings'),
                  on: {
                    close() {
                      emit('close');
                      localState.showPopup = null;
                      modalComposition.modal.hide('ProgramSettings');
                    }
                  }
                }
              }
            });
          }
        }
      ]
    });

    const getPicture = () => {
      if (user.value.mIcon) {
        return getImageById(user.value.mIcon);
      }

      if (user.value.mPicture) {
        return getImageById(user.value.mPicture);
      }

      return '';
    };

    const firstLetters = computed(() => {
      if (user.value?.login) {
        const r = user.value.login.split(' ');
        return r.length > 1
          ? `${r[0][0]}${r[1][0]}`.toUpperCase()
          : `${r[0][0]}`.toUpperCase();
      }
      return 'UN';
    });
    const notificationProfiles = computed(() => {
      return profile.value.objectsToObjectsByObject1Id.map(
        item => item.object2
      );
    });

    const isShowEmailNotifications = computed(() => {
      const channelIn = notificationProfiles.value.find(
        item => item.deliveryChannel === 'Email'
      );
      return channelIn;
    });

    const isShowInAppNotifications = computed(() => {
      const channelIn = notificationProfiles.value.find(
        item => item.deliveryChannel === 'In-app'
      );

      return channelIn;
    });
    const isShowSmsNotifications = computed(() => {
      const channelIn = notificationProfiles.value.find(
        item => item.deliveryChannel === 'SMS (Twilio)'
      );

      return channelIn;
    });

    const isShowWhatsAppNotifications = computed(() => {
      const channelIn = notificationProfiles.value.find(
        item => item.deliveryChannel === 'WhatsApp (Twilio)'
      );

      return channelIn;
    });
    const isShowTelegramNotifications = computed(() => {
      const channelIn = notificationProfiles.value.find(
        item => item.deliveryChannel === 'Telegram'
      );

      return channelIn;
    });

    onMounted(() => {
      const action = target => {
        modalComposition.modal.show({
          component: () =>
            import('@/components/app-entity/modals/AppDialog.vue'),
          bind: {
            nameModal: 'NotificationSettings'
          },
          slots: {
            default: {
              component: () => import('../popups/NotificationSettings'),
              bind: {
                profile: target
              },
              on: {
                close() {
                  emit('close');
                  localState.showPopup = null;
                  modalComposition.modal.hide('NotificationSettings');
                }
              }
            }
          }
        });
      };

      if (isShowInAppNotifications.value) {
        localState.items.push({
          id: 'notification_settings_in_app',
          title: 'Notification settings',
          icon: 'mdi-bell-plus',
          action: () => action(isShowInAppNotifications.value)
        });
      }

      if (isShowSmsNotifications.value) {
        localState.items.push({
          id: 'notification_settings_sms',
          title: 'SMS (Twilio)',
          icon: 'mdi-message-alert',
          action: () => action(isShowSmsNotifications.value)
        });
      }
      if (isShowWhatsAppNotifications.value) {
        localState.items.push({
          id: 'notification_settings_whatsap',
          title: 'WhatsApp (Twilio)',
          icon: 'mdi-whatsapp',
          action: () => action(isShowWhatsAppNotifications.value)
        });
      }
      if (isShowEmailNotifications.value) {
        localState.items.push({
          id: 'notification_settings_email',
          title: 'E-mail notifications',
          icon: 'mdi-email',
          action: () => action(isShowEmailNotifications.value)
        });
      }
      if (isShowTelegramNotifications.value) {
        localState.items.push({
          id: 'notification_settings_telegram',
          title: 'Telegram notifications',
          icon: 'mdi-send',
          action: () => action(isShowTelegramNotifications.value)
        });
      }

      localState.items.push({
        id: 'logout',
        title: 'Logout',
        icon: 'mdi-exit-to-app',
        action: () => {
          localStorage.removeItem('tokenId');
          localStorage.removeItem('authToken');
          localStorage.removeItem('refreshToken');
          root.$router.push('/login');
          emit('close');
        }
      });
    });

    return {
      ...toRefs(localState),
      firstLetters,
      user,
      getPicture,
      notificationProfiles
    };
  }
};
</script>

<style>
.menu-user-banner {
  padding: 0 20px;
  box-shadow: 0 4px 4px rgba(0, 0, 0, 0.25);
  background-color: #333;
  height: 56px;
  font-size: 20px;
  color: white !important;
}
</style>
